// Load Dependencies
import React, {Component} from "react";
import {connect} from "react-redux";

import {configurationsStoreSelectors} from "../../../../store/store";

import {ActionCreator} from "../../../../../../actions/actions";
import {ConfigurationsActionCreators} from "../../../../actions/actionCreators";
import {Components} from "@webtech/webtech-ui-react/build/webtech-ui";

import "./MegapotConfigurationSection.component.scss";
import {ConfigurationsActionTypes} from "../../../../actions/actionTypes";

import FormBoxes from "../../../../../../utils/forms/components/FormBoxes";
import {megapotConfigBox} from "./FormBuild/MegapotConfigurationSection.forms";
import {
    checkDefaultForType,
    checkRenderForProvider,
} from "../../../../../../utils/utils";

export interface MegapotConfigurationSectionProps {
    configSectionVisited: ActionCreator;
    megapotSchema: any;
    schemaLoading: boolean;
    editingConfigurationLoading: boolean;
    updateMegapotConfig: ActionCreator;
    megapotConfig: any;
    getMegapotServerList: ActionCreator;
    getMegapotProvidersList: ActionCreator;
    editingConfiguration: any;
    megapotServersLoading: boolean;
    megapotServers: any;
    megapotProvidersLoading: boolean;
    megapotProviders: any;
}

export interface MegapotConfiguration {
}

class MegapotConfigurationSection extends Component<
    MegapotConfigurationSectionProps,
    MegapotConfiguration
> {
    private lastProviderId: any = null;

    constructor(props: MegapotConfigurationSectionProps) {
        super(props);
        // Seed lastProviderId from existing config so the first onChange doesn't wipe the server
        this.lastProviderId = this.getProviderId(props.megapotConfig);
    }

    private getProviderId = (v?: any) => {
        if (!v) return null;

        // Prefer v.provider, fallback to v.providerId
        var provider =
            (typeof v.provider !== "undefined" && v.provider !== null)
                ? v.provider
                : (typeof v.providerId !== "undefined" && v.providerId !== null)
                    ? v.providerId
                    : null;

        if (provider && typeof provider === "object") {
            return provider.id || provider.idProvider || provider.providerId || null;
        }
        return provider;
    };

    private getSubsidiaryIdFromProps = (p: MegapotConfigurationSectionProps) => {
        const ec: any = p.editingConfiguration;
        return ec && ec.subsidiary ? ec.subsidiary.id : undefined;
    };

    componentDidMount() {
        this.props.configSectionVisited(
            ConfigurationsActionTypes.UPDATE_MEGAPOT_CONFIG
        );

        this.props.getMegapotProvidersList();

        // If we already have a provider + subsidiary, preload server list
        const providerId = this.getProviderId(this.props.megapotConfig);
        const subsidiaryId = this.getSubsidiaryIdFromProps(this.props);
        if (providerId && subsidiaryId) {
            this.props.getMegapotServerList(providerId, subsidiaryId);
        }
    }

    componentDidUpdate(prevProps: MegapotConfigurationSectionProps) {
        // Keep lastProviderId in sync if config is replaced from Redux
        if (prevProps.megapotConfig !== this.props.megapotConfig) {
            const providerId = this.getProviderId(this.props.megapotConfig);
            this.lastProviderId = providerId;
        }
    }

    onFormChange = (props: { values: any; isValid: boolean }) => {
        if (this.props.schemaLoading || this.props.editingConfigurationLoading) return;

        var values = {...props.values};
        var valid = props.isValid;

        values.serverAPI = null;

        if (values.enabled === false) {
            values.address = null;
            values.server = null;
        } else {
            if (values.server == null) valid = false;
        }

        const providerId = this.getProviderId(values);
        const subsidiaryId = this.getSubsidiaryIdFromProps(this.props);

        // First run after mount (or when lastProviderId not set yet):
        // record provider & (optionally) fetch servers, but don't clear server
        if (this.lastProviderId == null) {
            this.lastProviderId = providerId;
            if (providerId && subsidiaryId) {
                this.props.getMegapotServerList(providerId, subsidiaryId);
            }
        } else if (providerId !== this.lastProviderId) {
            // True provider change by the user → clear server and reload options
            values.server = null;
            valid = false;
            this.lastProviderId = providerId;

            if (providerId && subsidiaryId) {
                this.props.getMegapotServerList(providerId, subsidiaryId);
            }
        }

        if (values.server != null && Array.isArray(this.props.megapotServers)) {
            const found = this.props.megapotServers.find(
                (s: any) => s.idServer === values.server
            );
            if (found) values.serverAPI = found;
        }

        this.props.updateMegapotConfig({
            config: values,
            isValid: valid,
        });
    };

    render() {
        const schema = this.props.megapotSchema;

        if (this.props.editingConfigurationLoading || this.props.megapotServersLoading) {
            return (
                <div className="loading" key="loading">
                    <Components.LoadingRipple width={100} height={100}/>
                </div>
            );
        }

        // Build defaults from schema
        var defaults: any = {};
        Object.keys(schema.properties).forEach((item) => {
            defaults[item] =
                checkRenderForProvider(item, schema.properties) && schema.properties[item]
                    ? Object.prototype.hasOwnProperty.call(schema.properties[item], "default")
                        ? schema.properties[item].convert
                            ? schema.properties[item].default / 100
                            : schema.properties[item].default
                        : checkDefaultForType(
                            schema.properties[item].type,
                            undefined,
                            schema.required && schema.required.includes(item)
                        )
                    : null;
            // DO NOT reset defaults.server here
            // defaults["server"] = null;
        });

        // Prefer persisted config from Redux if available
        if (this.props.megapotConfig && Object.keys(this.props.megapotConfig).length > 0) {
            defaults = this.props.megapotConfig;
        }

        return (
            <div id="MegapotConfigurationSection">
                <FormBoxes
                    boxes={megapotConfigBox(
                        schema,
                        this.props.megapotProviders,
                        this.props.megapotServers,
                        this.props.megapotConfig ? this.props.megapotConfig : null
                    )}
                    onChange={this.onFormChange}
                    validateOnUpdate={true}
                    initialValues={defaults}
                    isConfig={true}
                />
            </div>
        );
    }
}

const mapStateToProps = (state: any) => ({
    megapotSchema: configurationsStoreSelectors.megapotSchema(state),
    schemaLoading: configurationsStoreSelectors.schemaLoading(state),
    editingConfigurationLoading:
        configurationsStoreSelectors.editingConfigurationLoading(state),
    megapotConfig: configurationsStoreSelectors.megapotConfig(state),
    editingConfiguration: configurationsStoreSelectors.editingConfiguration(state),
    megapotServersLoading: configurationsStoreSelectors.megapotServersLoading(state),
    megapotServers: configurationsStoreSelectors.megapotServers(state),
    megapotProvidersLoading:
        configurationsStoreSelectors.megapotProvidersLoading(state),
    megapotProviders: configurationsStoreSelectors.megapotProviders(state),
});

const mapDispatchToProps = {
    configSectionVisited: ConfigurationsActionCreators.configSectionVisited,
    updateMegapotConfig: ConfigurationsActionCreators.updateMegapotConfig,
    getMegapotServerList: ConfigurationsActionCreators.getMegapotServerList,
    getMegapotProvidersList: ConfigurationsActionCreators.getMegapotProvidersList,
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(MegapotConfigurationSection);
